import React, { useState } from "react";
import { Modal, makeStyles, Theme, Grid, Typography } from "@material-ui/core";
import { MyPagePaper } from "./MyPagePaper";
import { FormattedMessage, FormattedDate, useIntl } from "react-intl";
import MyPageButton from "./MyPageButton";
import { useFetchWithToken } from "../../hooks";
import { BackofficeApplicationResource } from "../../resources";
import MyPageDatePicker from "./MyPageDatePicker";
import { navigate } from "@reach/router";

interface MyPageModalProps {
	modalContent:
		| "FulfillContract"
		| "biggerPayment"
		// | "extendLoan"
		// | "cancelLoan"
		| string;
	children?: React.ReactNode;
	setModalContent: (val: string) => void;
	backofficeApplication: BackofficeApplicationResource;
}

const useStyles = makeStyles((theme: Theme) => ({
	modal: {
		position: "absolute",
		width: "100%",
		backgroundColor: "rgba(0,0,0,0.5)",
		padding: theme.spacing(3),
		display: "flex",
		overflowY: "auto",
	},
	container: {
		margin: "auto",
		[theme.breakpoints.up("md")]: {
			width: 600,
		},
	},
	textField: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		width: 200,
	},
	boldText: {
		fontWeight: "bold",
	},
}));

const MyPageModal = (props: MyPageModalProps) => {
	const { modalContent, setModalContent } = props;

	const classes = useStyles();

	const handleClose = () => setModalContent("");

	return (
		<Modal
			open={modalContent !== ""}
			className={classes.modal}
			onClose={handleClose}
		>
			<div className={classes.container}>
				<MyPagePaper>
					<ModalFormInternal
						modalContentInternal={modalContent}
						handleClose={handleClose}
						classes={classes}
						{...props}
					/>
				</MyPagePaper>
			</div>
		</Modal>
	);
};

interface ModalFormProps {
	modalContentInternal: string;
	handleClose: () => void;
	classes: any;
	backofficeApplication: BackofficeApplicationResource;
}

const ModalFormInternal = (props: ModalFormProps) => {
	const { modalContentInternal } = props;
	switch (modalContentInternal) {
		case "FulfillContract":
			return <FulfillContractForm {...props} />;
		case "biggerPayment":
			return <BiggerPaymentForm {...props} />;
		// case "extendLoan":
		// 	return <ExtendLoanForm {...props} />;
		// case "cancelLoan":
		// 	return <CancelLoanForm {...props} />;
		default:
			return <h1>Innhold her</h1>;
	}
};

const FulfillContractForm = (props: ModalFormProps) => {
	const { handleClose, classes, backofficeApplication } = props;

	const today = new Date();
	const [date, setDate] = useState(today);
	const [remainingCost, setRemainingCost] = useState(0);
	const [error, setError] = useState("");
	const intl = useIntl();

	const adminFetcher = useFetchWithToken("/api/administrations/");
	const calculateRemainingCost = async () => {
		const res = await adminFetcher("calculateRemainingCost", {
			method: "post",
			body: JSON.stringify({
				fulfillmentDate: date,
				backofficeApplicationId: backofficeApplication.id,
			}),
		});
		if (res.status === 401) {
			navigate("/logout", { state: { errorCode: 401 } });
			return null;
		}
		return await res.json();
	};

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Typography variant="h4">
					<FormattedMessage
						id="minside.MyPageModal.FulfillContractForm.title"
						defaultMessage="Innfrielse av kontrakt"
						description="Header text in MyPageModal.RedeemContract"
					/>
				</Typography>
			</Grid>

			<Grid item>
				<FormattedMessage
					id="minside.MyPageModal.FulfillContractForm.description"
					defaultMessage="For å betale ned hele lånet må du oppgi en dato for innfrielse. Det vil si den datoen du ønsker å gjøre innbetalingen. Datoen du oppgir danner grunnlaget for vår beregning av det riktige beløpet for innfrielse"
					description="FulfillContractForm's form description"
				/>
			</Grid>

			<Grid item>
				<MyPageDatePicker
					value={date}
					maxDate={new Date().setDate(today.getDate() + 30)}
					onChangeDate={(newDate: any) => setDate(newDate)}
					disablePast
				/>
			</Grid>

			{remainingCost > 0 && (
				<Grid item container spacing={1}>
					<Grid item>
						<Typography variant="h6">
							<FormattedMessage
								id="minside.MyPageModal.FulfillContractForm.paymentInformation.header"
								defaultMessage="Betalingsinformasjon"
								description="FulfillContractForm.Paymentinformation.Header"
							/>
						</Typography>
						<ul>
							<li>
								<FormattedMessage
									id="minside.MyPageModal.FulfillContractForm.paymenInformation.remainingCost"
									defaultMessage="Innfrielsessum pr. {date}: kr {remainingCost},-"
									description="FulfillContractForm.PaymentInformation.RemainingCostPerDate"
									values={{
										date: (
											<FormattedDate
												year="numeric"
												month="2-digit"
												day="2-digit"
												value={date}
											/>
										),
										remainingCost: Intl.NumberFormat(intl.locale, {
											maximumFractionDigits: 2,
										}).format(remainingCost),
									}}
								/>
							</li>
							<li>
								<FormattedMessage
									id="minside.MyPageModal.FulfillContractForm.paymenInformation.accountNumber"
									defaultMessage="Kontonummer: 2220 19 95578"
									description="FulfillContractForm.PaymentInformation.AccountNumber"
								/>
							</li>
							<li>
								<FormattedMessage
									id="minside.MyPageModal.FulfillContractForm.paymenInformation.kidNr"
									defaultMessage="KID: {kidNr}"
									description="FulfillContractForm.PaymentInformation.KidNr"
									values={{
										kidNr: backofficeApplication.kidNrFromLastInvoice || " - ",
									}}
								/>
							</li>
						</ul>
					</Grid>
					<Grid item>
						<FormattedMessage
							id="minside.MyPageModal.FulfillContractForm.paymentInformation.information"
							defaultMessage="Salgspant i registreringsnummer {regNumber} vil bli slettet ved innfrielse innen oppgitte dato. Vi gjør oppmerksom på at det kan ta inntil 1 uke før sletting blir registrert hos Løsøreregisteret."
							description="FulfillContractForm.PaymentInformation.Information"
							values={{ regNumber: backofficeApplication.regNumber }}
						/>
					</Grid>
					<Grid item>
						<FormattedMessage
							id="minside.MyPageModal.FulfillContractForm.paymentInformation.eFaktura_reminder"
							defaultMessage="Husk å stoppe eventuelle avtalegiro/trekk i nettbanken din ved innfrielse av lånet."
							description="FulfillContractForm.PaymentInformation.eFaktura_reminder"
						/>
					</Grid>
				</Grid>
			)}

			{error && <Grid item>{error}</Grid>}

			<Grid
				item
				container
				direction="row"
				justify="space-between"
				alignItems="center"
			>
				<MyPageButton actionType="cancel" onClick={() => handleClose()}>
					Lukk
				</MyPageButton>
				{remainingCost > 0 && (
					<Typography className={classes.boldText}>
						<FormattedMessage
							id="minside.MyPageModal.FulfillContractForm.paymentInformation.price"
							defaultMessage="Beløp å betale: {remainingCost}"
							description="Remaining sum to pay"
							values={{
								remainingCost: Intl.NumberFormat(intl.locale, {
									style: "currency",
									currency: "NOK",
									maximumFractionDigits: 0,
									minimumFractionDigits: 0,
								}).format(remainingCost),
							}}
						/>
					</Typography>
				)}
				<MyPageButton
					actionType="action"
					onClick={async () => {
						await calculateRemainingCost().then((obj) => {
							setRemainingCost(obj.remainingCost);
							setError(obj.error);
						});
					}}
					disabled={!date}
				>
					Beregn
				</MyPageButton>
			</Grid>
		</Grid>
	);
};

const BiggerPaymentForm = (props: ModalFormProps) => {
	const { handleClose } = props;

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Typography variant="h4">
					<FormattedMessage
						id="minside.MyPageModal.BiggerPaymentForm.Title"
						defaultMessage="Betal inn ekstra"
						description="Header text in MyPageModal.BiggerPaymentForm"
					/>
				</Typography>
			</Grid>

			<Grid item>
				<Typography>Ønsker du å betale inn ekstra på lånet ditt?</Typography>
				<Typography>
					Benytt kontonr <strong>2220 19 95578</strong> og KID som du finner på siste faktura.
					Alle ekstraordinære innbetalinger vil bli ført som nedbetaling på lånet ditt og medfører kortere nedbetalingstid. Ditt månedlige terminbeløp vil være som før.
					Merk at alle innbetalinger kan gå til å dekke ubetalte fakturaer. Har du spørsmål, ta kontakt med oss på e-post <a href="mailto:okonomi@financiering.no">okonomi@financiering.no</a>
				</Typography>
			</Grid>

			<Grid
				item
				container
				direction="row"
				justify="flex-end"
				alignItems="center"
			>
				<MyPageButton actionType="action" onClick={() => handleClose()}>
					<FormattedMessage
						id="minside.components.modal.close"
						description="Close button modal"
						defaultMessage="Lukk"
					/>
				</MyPageButton>
			</Grid>
		</Grid>
	);
};

// const ExtendLoanForm = (props: ModalFormProps) => {
// 	// const { modalContent, handleClose, classes } = props;

// 	return (
// 		<Grid container direction="column" spacing={3}>
// 			<Grid item>
// 				<Typography variant="h4">
// 					<FormattedMessage
// 						id="minside.MyPageModal.title_extendLoanForm"
// 						defaultMessage="Utsett nedbetaling"
// 						description="Header text in MyPageModal"
// 					/>
// 				</Typography>
// 			</Grid>
// 			<Grid item>
// 				<h1>Skjema for å utsette nedbetaling</h1>
// 			</Grid>
// 		</Grid>
// 	);
// };

// const CancelLoanForm = (props: ModalFormProps) => {
// 	// const { modalContent, handleClose, classes } = props;

// 	return (
// 		<Grid container direction="column" spacing={3}>
// 			<Grid item>
// 				<Typography variant="h4">
// 					<FormattedMessage
// 						id="minside.MyPageModal.title_cancelLoanForm"
// 						defaultMessage="Avslutte avtale"
// 						description="Header text in MyPageModal"
// 					/>
// 				</Typography>
// 			</Grid>
// 			<Grid item>
// 				<h1>Skjema for å kansellere avtale</h1>
// 			</Grid>
// 		</Grid>
// 	);
// };

export default MyPageModal;
